import React from "react";
import PropTypes from "prop-types";
import ScreenshotInput from "./ScreenshotInput";
import { PriceGuaranteeModalsErrors } from "./PriceGuaranteeModalDefinitions";

import(/* webpackChunkName: "forms" */ "~/less/forms.less");

interface PriceGuaranteeModalProps {
  email: string;
  phoneNumber: string;
  priceToMatch: string | number;
  externalLink: string;
  screenshotName: string;
  screenshotAllowedMimeTypes: string[];
  onEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPhoneNumberChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPriceToMatchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onExternalLinkChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onScreenshotChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  readOnly?: boolean;
  errors: PriceGuaranteeModalsErrors;
  submitSuccessMessage?: string;
}

const PriceGuaranteeModal = ({
  email,
  phoneNumber,
  priceToMatch,
  externalLink,
  screenshotName,
  screenshotAllowedMimeTypes,
  onEmailChange,
  onPhoneNumberChange,
  onPriceToMatchChange,
  onExternalLinkChange,
  onScreenshotChange,
  readOnly = false,
  errors = {
    email: "",
    phoneNumber: "",
    priceToMatch: "",
    externalLink: "",
    screenshot: "",
    global: "",
  },
  submitSuccessMessage = "",
}: PriceGuaranteeModalProps) => {
  return (
    <>
      <p className="small">
        We guarantee this is the lowest price for this item.
      </p>

      <div className="form">
        {errors.global && (
          <div className="alert alert-danger">{errors.global}</div>
        )}

        {submitSuccessMessage && (
          <div className="alert alert-success">{submitSuccessMessage}</div>
        )}

        <div className="form__group">
          <div className="form__control-container">
            <input
              type="text"
              className="form__control"
              id="priceGaurantee_emailAddress"
              name="priceGaurantee_emailAddress"
              placeholder="Email Address"
              maxLength={255}
              value={email}
              onChange={onEmailChange}
              readOnly={readOnly}
            />
            <label
              className="form__label"
              htmlFor="priceGaurantee_emailAddress"
            >
              Email Address
            </label>
          </div>
          {errors.email && <div className="form__error">{errors.email}</div>}
        </div>

        <div className="form__group">
          <div className="form__control-container">
            <input
              type="text"
              className="form__control"
              id="priceGaurantee_phoneNumber"
              name="priceGaurantee_phoneNumber"
              placeholder="Phone Number"
              maxLength={25}
              value={phoneNumber}
              onChange={onPhoneNumberChange}
              readOnly={readOnly}
            />
            <label className="form__label" htmlFor="priceGaurantee_phoneNumber">
              Phone Number
            </label>
          </div>
        </div>

        <div className="form__group">
          <div className="form__control-container">
            <input
              type="number"
              className="form__control"
              id="priceGaurantee_itemPrice"
              name="priceGaurantee_itemPrice"
              placeholder="Item Price"
              min="0.00"
              step="0.01"
              value={priceToMatch}
              onChange={onPriceToMatchChange}
              readOnly={readOnly}
            />
            <label className="form__label" htmlFor="priceGaurantee_itemPrice">
              Item Price
            </label>
          </div>
          {errors.priceToMatch && (
            <div className="form__error">{errors.priceToMatch}</div>
          )}
        </div>

        <div className="form__group">
          <div className="form__control-container">
            <input
              type="url"
              className="form__control"
              id="priceGaurantee_link"
              name="priceGaurantee_link"
              placeholder="Link to Item"
              maxLength={255}
              value={externalLink}
              onChange={onExternalLinkChange}
              readOnly={readOnly}
            />
            <label className="form__label" htmlFor="priceGaurantee_link">
              Link to Item
            </label>
          </div>
          {errors.externalLink && (
            <div className="form__error">{errors.externalLink}</div>
          )}
        </div>

        <ScreenshotInput
          name={screenshotName}
          allowedMimeTypes={screenshotAllowedMimeTypes}
          readOnly={readOnly}
          error={errors.screenshot}
          onChange={onScreenshotChange}
        />
      </div>
    </>
  );
};

PriceGuaranteeModal.propTypes = {
  email: PropTypes.string.isRequired,
  phoneNumber: PropTypes.string.isRequired,
  priceToMatch: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  externalLink: PropTypes.string.isRequired,
  screenshotName: PropTypes.string.isRequired,
  errors: PropTypes.shape({
    email: PropTypes.string,
    phoneNumber: PropTypes.string,
    priceToMatch: PropTypes.string,
    externalLink: PropTypes.string,
    screenshot: PropTypes.string,
    global: PropTypes.string,
  }),
  readOnly: PropTypes.bool,
  submitSuccessMessage: PropTypes.string,
  screenshotAllowedMimeTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  onEmailChange: PropTypes.func.isRequired,
  onPhoneNumberChange: PropTypes.func.isRequired,
  onPriceToMatchChange: PropTypes.func.isRequired,
  onExternalLinkChange: PropTypes.func.isRequired,
  onScreenshotChange: PropTypes.func.isRequired,
};

PriceGuaranteeModal.defaultProps = {
  errors: {
    email: "",
    phoneNumber: "",
    priceToMatch: "",
    externalLink: "",
    screenshot: "",
    global: "",
  },
  readOnly: false,
  submitSuccessMessage: "",
};

export default PriceGuaranteeModal;
